import { useMemo } from "react";
import { Button, Form, Modal, Popconfirm, Select, Table, Input, Space, Spin, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Bodega } from "../../services/propiedades.api";
import { useBodegasLogic, BodegaFormValues } from "./useBodegasLogic";
import { paginacionCliente } from "../../utils/tablaPaginacion";

const { Text } = Typography;

export default function BodegasTab() {
  const {
    bodegas,
    loading,
    modalOpen,
    editing,
    catalogsLoading,
    paisOptions,
    regionOptions,
    comunaOptions,
    sectorOptions,
    openCreate,
    openEdit,
    closeModal,
    createBodega,
    updateBodega,
    deleteBodega,
  } = useBodegasLogic();

  const [form] = Form.useForm<BodegaFormValues>();

  const paisById = useMemo(
    () => new Map(paisOptions.map((p) => [p.id, p.pais])),
    [paisOptions],
  );
  const regionById = useMemo(
    () => new Map(regionOptions.map((r) => [r.id, r.region])),
    [regionOptions],
  );
  const comunaById = useMemo(
    () => new Map(comunaOptions.map((c) => [c.id, c.comuna])),
    [comunaOptions],
  );
  const sectorById = useMemo(
    () => new Map(sectorOptions.map((s) => [s.id, s.sector])),
    [sectorOptions],
  );

  const renderGeo = (id: number | null | undefined, map: Map<number, string>) => {
    if (!id) return "-";
    if (catalogsLoading && map.size === 0) return <Spin size="small" />;
    const name = map.get(id);
    return name ?? <Text type="secondary">ID: {id}</Text>;
  };

  const columns: ColumnsType<Bodega> = [
    {
      title: "Nombre",
      dataIndex: "bodega",
      key: "bodega",
    },
    {
      title: "País",
      dataIndex: "pais",
      key: "pais",
      render: (v: number) => renderGeo(v, paisById),
    },
    {
      title: "Región",
      dataIndex: "region",
      key: "region",
      render: (v: number) => renderGeo(v, regionById),
    },
    {
      title: "Comuna",
      dataIndex: "comuna",
      key: "comuna",
      render: (v: number) => renderGeo(v, comunaById),
    },
    {
      title: "Sector",
      dataIndex: "sector",
      key: "sector",
      render: (v: number) => renderGeo(v, sectorById),
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 120,
      render: (_: any, record: Bodega) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="¿Eliminar esta bodega?"
            onConfirm={() => deleteBodega(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleCreate = () => {
    form.resetFields();
    openCreate();
  };

  const handleEdit = (record: Bodega) => {
    form.setFieldsValue({
      bodega: record.bodega,
      pais: record.pais,
      region: record.region,
      comuna: record.comuna,
      sector: record.sector,
    });
    openEdit(record);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (editing) {
      await updateBodega(editing.id, values);
    } else {
      await createBodega(values);
    }
  };

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Nueva Bodega
        </Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={bodegas}
        loading={loading}
        pagination={paginacionCliente({ porDefecto: 10, etiqueta: "bodega" })}
        size="middle"
      />

      <Modal
        title={editing ? "Editar Bodega" : "Nueva Bodega"}
        open={modalOpen}
        onCancel={() => {
          closeModal();
          form.resetFields();
        }}
        onOk={handleSubmit}
        okText={editing ? "Guardar" : "Crear"}
        cancelText="Cancelar"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Nombre"
            name="bodega"
            rules={[{ required: true, message: "Ingresa el nombre de la bodega" }]}
          >
            <Input placeholder="Nombre de la bodega" />
          </Form.Item>

          <Form.Item
            label="País"
            name="pais"
            rules={[{ required: true, message: "Selecciona un país" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Selecciona país"
              loading={catalogsLoading}
              options={paisOptions.map((p) => ({ value: p.id, label: p.pais }))}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="Región"
            name="region"
            rules={[{ required: true, message: "Selecciona una región" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Selecciona región"
              loading={catalogsLoading}
              options={regionOptions.map((r) => ({ value: r.id, label: r.region }))}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="Comuna"
            name="comuna"
            rules={[{ required: true, message: "Selecciona una comuna" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Selecciona comuna"
              loading={catalogsLoading}
              options={comunaOptions.map((c) => ({ value: c.id, label: c.comuna }))}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="Sector"
            name="sector"
            rules={[{ required: true, message: "Selecciona un sector" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Selecciona sector"
              loading={catalogsLoading}
              options={sectorOptions.map((s) => ({ value: s.id, label: s.sector }))}
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
